import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    Box,
    Typography,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from "@mui/material";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BracketRound from "../components/brackets/BracketRound";
import ExportButtons from "../components/brackets/ExportButtons";
import RankingTable from "../components/brackets/RankingTable";
import PairingSheet from "../components/brackets/PairingSheet";
import LiveChronoFeed from "../components/brackets/LiveChronoFeed";
import FisStatusMini from "../components/timingsoftware/FisStatusMini";
import { authHeader } from "../auth";
import { usePresencePing } from "../hooks/usePresencePing";
import { useParallelBranding } from "../utils/useParallelBranding";
import { deriveRanking } from "../utils/ranking";
import ParallelSubNav from "./ParallelSubNav";

export default function Brackets() {
    const { category } = useParams();
    const navigate = useNavigate();
    usePresencePing(category, "Bracket");
    const branding = useParallelBranding(category);
    const [bracket, setBracket] = useState(null);
    const [error, setError] = useState("");
    const [view, setView] = useState("duels"); // "duels" | "ranking" | "pairing"

    // Archivage : déplace la course hors de la liste des courses
    // parallèles actives, en conservant côté serveur l'intégralité des
    // données de chronométrage (bracket, résultats, écarts, photofinish,
    // décisions du jury) — ce n'est pas une suppression.
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
    const [archiving, setArchiving] = useState(false);

    const loadBracket = useCallback(async () => {
        try {
            const res = await fetch(`/api/finals/${category}`);
            const data = await res.json();
            setBracket(data && data.rounds ? data : null);
        } catch (err) {
            setError(err.message);
        }
    }, [category]);

    useEffect(() => {
        loadBracket();
    }, [loadBracket]);

    // Tant qu'un duel est marqué "en cours", on réinterroge régulièrement
    // le bracket — c'est la détection automatique côté serveur
    // (registerFinishArrival) qui remplit le vainqueur dès que les 2
    // arrivées chrono sont enregistrées, sans action du chronométreur.
    useEffect(() => {
        if (!bracket?.activeMatchId) return;
        const interval = setInterval(loadBracket, 3000);
        return () => clearInterval(interval);
    }, [bracket?.activeMatchId, loadBracket]);

    const handleWinnerSelected = async (matchId, athlete) => {
        try {
            const res = await fetch(`/api/finals/set-winner/${category}/${matchId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ winner: athlete }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            setError("");
            setBracket(await res.json());
        } catch (err) {
            setError(err.message);
        }
    };

    const handleSetActive = async (matchId) => {
        try {
            const res = await fetch(`/api/finals/set-active-match/${category}/${matchId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            setError("");
            setBracket(await res.json());
        } catch (err) {
            setError(err.message);
        }
    };

    const handleClearActive = async () => {
        try {
            const res = await fetch(`/api/finals/clear-active-match/${category}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            setError("");
            setBracket(await res.json());
        } catch (err) {
            setError(err.message);
        }
    };

    const handlePhotoFinishToggle = async (matchId, photoFinish) => {
        try {
            const res = await fetch(`/api/finals/set-photofinish/${category}/${matchId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ photoFinish }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            loadBracket();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleJuryDecisionToggle = async (matchId, juryDecision) => {
        try {
            const res = await fetch(`/api/finals/set-jury-decision/${category}/${matchId}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
                body: JSON.stringify({ juryDecision }),
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            loadBracket();
        } catch (err) {
            setError(err.message);
        }
    };

    // Archive la course côté serveur (toutes les données de chronométrage
    // sont conservées, seule la visibilité dans la liste des courses
    // actives change) puis retourne à la liste des courses parallèles.
    const handleArchive = async () => {
        setArchiving(true);
        try {
            const res = await fetch(`/api/finals/archive/${category}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", ...authHeader() },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                setArchiving(false);
                setArchiveDialogOpen(false);
                return;
            }

            setArchiveDialogOpen(false);
            navigate("/parallel");
        } catch (err) {
            setError(err.message);
            setArchiving(false);
            setArchiveDialogOpen(false);
        }
    };

    if (!bracket) {
        return (
            <Box p={3}>
                <ParallelSubNav category={category} current="brackets" />
                <Typography>{error ? `Erreur : ${error}` : "Chargement du bracket…"}</Typography>
            </Box>
        );
    }

    const finalRanking = deriveRanking(bracket);

    const scrollToRound = (anchorId) => {
        document.getElementById(anchorId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <Box p={3}>
            {/* Isole la zone imprimable : masque TOUT le reste de la page
                (nav du site, header/footer compris) au moment de l'impression,
                quel que soit ce qui entoure ce composant. */}
            <style>{`
                @media print {
                    body * { visibility: hidden; }
                    .printable-area, .printable-area * { visibility: visible; }
                    .printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                    }
                    /* visibility:hidden masque mais garde l'espace occupé
                       — la nav et le pied de page du site laissaient donc
                       un grand vide avant le contenu imprimé. On force
                       leur hauteur à 0 explicitement. */
                    .MuiAppBar-root, .app-footer, nav {
                        display: none !important;
                    }
                }
            `}</style>

            {/* Sous-navigation des pages de la course (masquée à l'impression) */}
            <ParallelSubNav category={category} current="brackets" />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    flexWrap: "wrap",
                    gap: 1,
                    mb: 3,
                    "@media print": { display: "none" },
                }}
            >
                <Typography variant="h4">Bracket — {category}</Typography>

                <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    startIcon={<Inventory2Icon />}
                    onClick={() => setArchiveDialogOpen(true)}
                >
                    Archiver la course
                </Button>
            </Box>

            <Dialog open={archiveDialogOpen} onClose={() => (archiving ? null : setArchiveDialogOpen(false))}>
                <DialogTitle>Archiver « {category} » ?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Cette course sera retirée de la liste des courses parallèles actives.
                        Toutes les données de chronométrage (bracket, résultats de chaque duel,
                        écarts, photofinish, décisions du jury) sont conservées et restent
                        consultables depuis les archives — aucune donnée n'est supprimée.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiving}>
                        Annuler
                    </Button>
                    <Button onClick={handleArchive} color="error" variant="contained" disabled={archiving}>
                        {archiving ? "Archivage…" : "Archiver"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bascule entre les vues */}
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3, "@media print": { display: "none" } }}>
                <Chip
                    label="🥊 Duels (saisie)"
                    color={view === "duels" ? "primary" : "default"}
                    onClick={() => setView("duels")}
                />
                <Chip
                    label="🏆 Classement"
                    color={view === "ranking" ? "primary" : "default"}
                    onClick={() => setView("ranking")}
                />
                <Chip
                    label="📋 Tableau des paires"
                    color={view === "pairing" ? "primary" : "default"}
                    onClick={() => setView("pairing")}
                />
            </Box>

            {error && (
                <Typography color="error" mb={2} sx={{ "@media print": { display: "none" } }}>
                    {error}
                </Typography>
            )}

            {view === "ranking" && (
                <Box className="printable-area">
                    {finalRanking.length > 0 && (
                        <Box sx={{ "@media print": { display: "none" } }}>
                            <ExportButtons category={category} finalRanking={finalRanking} />
                        </Box>
                    )}
                    <RankingTable
                        ranking={finalRanking}
                        title={`Final ranking — ${category}`}
                        branding={branding}
                    />
                </Box>
            )}

            {view === "pairing" && (
                <Box className="printable-area">
                    <PairingSheet bracket={bracket} branding={branding} />
                </Box>
            )}

            {view === "duels" && (
                <>
                    <Box sx={{ "@media print": { display: "none" } }}>
                        <FisStatusMini eventKey={category} />
                    </Box>

                    <LiveChronoFeed channel="4" />

                    {bracket.activeMatchId && (
                        <Box
                            sx={{
                                backgroundColor: "#e8f5e9",
                                border: "1px solid #2e7d32",
                                borderRadius: 1,
                                p: 1.5,
                                mb: 2,
                            }}
                        >
                            <Typography sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                                🏁 Duel en cours : {bracket.activeMatchId} — le vainqueur sera rempli
                                automatiquement dès que les 2 arrivées chrono seront détectées.
                            </Typography>
                        </Box>
                    )}

                    {/* Navigation rapide vers un tour précis */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
                        {bracket.rounds.map((round, i) => (
                            <Chip
                                key={i}
                                label={round[0]?.label || `Tour ${i + 1}`}
                                clickable
                                onClick={() => scrollToRound(`round-${i}`)}
                            />
                        ))}
                        {bracket.smallFinal && (
                            <Chip
                                label="Petite finale (3e/4e)"
                                clickable
                                color="warning"
                                onClick={() => scrollToRound("round-small-final")}
                            />
                        )}
                    </Box>

                    {bracket.rounds.map((round, i) => (
                        <Box key={i} id={`round-${i}`}>
                            <BracketRound
                                round={round}
                                title={round[0]?.label}
                                onWinnerSelected={handleWinnerSelected}
                                onPhotoFinishToggle={handlePhotoFinishToggle}
                                onJuryDecisionToggle={handleJuryDecisionToggle}
                                onSetActive={handleSetActive}
                                onClearActive={handleClearActive}
                                activeMatchId={bracket.activeMatchId}
                            />
                        </Box>
                    ))}

                    {bracket.smallFinal && (
                        <Box id="round-small-final">
                            <BracketRound
                                round={[bracket.smallFinal]}
                                title="Petite finale (3e / 4e)"
                                onWinnerSelected={handleWinnerSelected}
                                onPhotoFinishToggle={handlePhotoFinishToggle}
                                onJuryDecisionToggle={handleJuryDecisionToggle}
                                onSetActive={handleSetActive}
                                onClearActive={handleClearActive}
                                activeMatchId={bracket.activeMatchId}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}